import { memo, useCallback, useMemo } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import "./css/NavBar.css";
import Dropdown from "./common/Dropdown";
import { Badge, Dropdown as BootstrapDropdown } from "react-bootstrap";
import { FaBell, FaEnvelope } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useGetUserNotification } from "../api/queries/useGetUserNotification";
import { useAppContext } from "../Context/AppContext";

interface NavBarProps {
  toggleSidebar: () => void;
}

interface NotificationButtonProps {
  onClick: () => void;
  count: number;
}

function NotificationButton({ onClick, count }: NotificationButtonProps) {
  return (
    <button
      onClick={onClick}
      className="position-relative cursor-pointer border-0 bg-transparent p-0"
    >
      <FaBell size={18} />
      {count > 0 && (
        <Badge
          bg="danger"
          pill
          className="position-absolute top-0 start-100 translate-middle"
          style={{ fontSize: "0.5rem" }}
        >
          {count}
        </Badge>
      )}
    </button>
  );
}

const memberBenefits = [
  { title: "BUSINESS MEMBERSHIP", href: "business-membership" },
  { title: "EXECUTIVE MEMBERSHIP", href: "executive-membership" },
  { title: "YOUTH MEMBERSHIP", href: "youth-membership" },
];

const internationalTrades = [
  {
    title: "EXECUTIVE INTERNATIONAL MEMBERSHIP",
    href: "executive-international-membership",
  },
  {
    title: "SAUDI ARABIA INTERNATIONAL TRADE PROGRAMME",
    href: "saudi-arabia-international-trade-programme",
  },
  {
    title: "FRANCE BUSINESS DELIGATION 2025",
    href: "france-business-delegation-2025",
  },
  {
    title: "TÜRKİYE INTERNATIONAL TRADE PROGRAMME",
    href: "smbf-turkiye-international-trade-programme",
  },
  {
    title: "DUBAI BUSINESS DELIGATION 2024",
    href: "dubai-business-delegation",
  },
];

function Navbar({ toggleSidebar }: NavBarProps) {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAppContext();
  const navigate = useNavigate();

  const { data: notifications } = useGetUserNotification({
    id: user?.user?.id ?? 0,
  });

  const unreadNotifications = useMemo(
    () =>
      notifications?.data?.filter(
        (item: { isRead: boolean }) => item.isRead === false
      ) || [],
    [notifications?.data]
  );

  const handleLogout = useCallback(async () => {
    localStorage.clear();
    await instance.logoutRedirect();
  }, [instance]);

  const handleNavigateToMessages = useCallback(() => {
    navigate("/messages");
  }, [navigate]);

  const handleNavigateToNotifications = useCallback(() => {
    navigate("/notifications");
  }, [navigate]);

  const handleNavigateToAccount = useCallback(() => {
    navigate("/my-account");
  }, [navigate]);

  const userProfileImage = useMemo(
    () =>
      user?.imageUrls.PersonalProfessionalPhoto ||
      user?.imageUrls.ProfessionalPhoto ||
      "/user.png",
    [user?.imageUrls.PersonalProfessionalPhoto, user?.imageUrls.ProfessionalPhoto]
  );

  const hasPaid = user?.user?.hasPaid === 1;
  const unreadCount = unreadNotifications.length;

  if (!isAuthenticated) {
    return (
      <nav className="navbar navbar-expand-lg navbar-light fixed-top">
        <div className="container">
          <a className="navbar-brand" href="/">
            <img
              src="/logo.png"
              className="logo"
              alt="SMBF Logo"
              width={125}
              height={80}
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </a>
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav me-auto">
              <li className="nav-item">
                <a className="nav-link" href="/our-vision">
                  Our Vision
                </a>
              </li>
              <li className="nav-item">
                <Dropdown
                  title="Member Benefits"
                  href="/members-benefits-smbf"
                  items={memberBenefits}
                />
              </li>
              <li className="nav-item">
                <a className="nav-link" href="/events">
                  Events
                </a>
              </li>
              <li className="nav-item">
                <Dropdown
                  title="International Trade"
                  href="/international-business-set-up-programme"
                  items={internationalTrades}
                />
              </li>
              <li className="nav-item">
                <a className="nav-link" href="/support-our-growth">
                  Support Us
                </a>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="navbar navbar-expand-lg navbar-light fixed-top p-0">
      <div className="d-flex justify-content-between w-100 p-0">
        <div
          className={`d-flex flex-lg-row justify-content-between px-1 px-md-4 ${
            hasPaid ? "nav-branding" : ""
          }`}
        >
          <a className="navbar-brand" href="/dashboard">
            <img
              src="/logo.png"
              className="logo"
              alt="SMBF Logo"
              width={125}
              height={80}
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </a>
          {hasPaid && (
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex d-md-none align-items-center gap-3">
                <button
                  onClick={handleNavigateToMessages}
                  className="position-relative cursor-pointer border-0 bg-transparent p-0"
                >
                  <FaEnvelope size={18} />
                </button>
                <NotificationButton
                  onClick={handleNavigateToNotifications}
                  count={unreadCount}
                />
              </div>
              <button
                className="navbar-toggler text-black d-block border-0 m-0 my-auto"
                type="button"
                onClick={toggleSidebar}
              >
                <span className="navbar-toggler-icon"></span>
              </button>
            </div>
          )}
        </div>

        <div className="d-flex align-items-center justify-content-end gap-4 w-100 px-4">
          {hasPaid && (
            <>
              <button
                onClick={handleNavigateToMessages}
                className="position-relative cursor-pointer border-0 bg-transparent p-0"
              >
                <FaEnvelope size={18} />
              </button>
              <NotificationButton
                onClick={handleNavigateToNotifications}
                count={unreadCount}
              />
            </>
          )}

          <BootstrapDropdown align="end">
            <BootstrapDropdown.Toggle
              variant="link"
              className="rounded-circle p-0"
              id="dropdown-custom-components"
            >
              <img
                src={userProfileImage}
                alt="User Profile"
                className="rounded-circle"
                width={40}
                height={40}
                loading="lazy"
                decoding="async"
                style={{ objectFit: "cover" }}
              />
            </BootstrapDropdown.Toggle>
            <BootstrapDropdown.Menu>
              <BootstrapDropdown.Item onClick={handleNavigateToAccount}>
                Profile
              </BootstrapDropdown.Item>
              <BootstrapDropdown.Item onClick={handleLogout}>
                Logout
              </BootstrapDropdown.Item>
            </BootstrapDropdown.Menu>
          </BootstrapDropdown>
        </div>
      </div>
    </nav>
  );
}

Navbar.displayName = "Navbar";

export default memo(Navbar);
